import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StorefrontAPI from 'utilities/storefrontApi';
import { mergeOnSignIn } from 'utilities/storefrontCartSync';
import { saveSession } from 'utilities/storefrontSession';
import {
  wrap, cardStyle, inputStyle, primaryBtn,
  navigateToAccount, navigateToSignup,
} from './styles';
import StorefrontAPIClient from 'utilities/storefrontApi';
import consumeOauthReturn from 'utilities/storefrontOauthReturn';
import SocialButtons from './SocialButtons';
import useBrandFromParams from './useBrandFromParams';
import BrandHeader from './BrandHeader';
import Centered from './Centered';

/**
 * Customer login for a brand storefront. Saves the per-brand session
 * (token + customer) so the customer stays signed in across page loads,
 * scoped by brand id (so a customer of brand A can simultaneously be a
 * customer of brand B without one stomping the other).
 */
export default function StorefrontLogin() {
  const { brand, error: brandError } = useBrandFromParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const accentColor = brand?.brand_color || '#422AFB';

  /**
   * Coming back from a provider.
   *
   * The callback leaves the session on the address because it redirects the
   * browser here rather than answering a fetch. Taken off the URL at once,
   * then the customer's profile is fetched with it so the session is stored
   * in the same shape a password login produces — otherwise the account page
   * has a token and no idea who it belongs to.
   */
  useEffect(() => {
    if (!brand?._id) return;
    const token = consumeOauthReturn();
    if (!token) return;
    saveSession(brand._id, { token, customer: null });
    StorefrontAPIClient.me(brand._id)
      .then((customer) => {
        saveSession(brand._id, { token, customer });
        // Their basket joins their account. Awaited before navigating so the
        // account page does not render against a cart about to change under
        // it; a failure leaves the local basket as it was.
        return mergeOnSignIn(brand._id).then(() => navigateToAccount(navigate, brand));
      })
      .catch(() => {
        // A token we cannot use is worse than none: it would leave the
        // account page half-signed-in with nothing on it.
        saveSession(brand._id, null);
        setError('That sign-in did not complete. Please try again.');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand?._id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!brand) return;
    setError('');
    setSubmitting(true);
    try {
      const out = await StorefrontAPI.login(brand._id, { email, password });
      saveSession(brand._id, { token: out.token, customer: out.customer });
      await mergeOnSignIn(brand._id);
      navigateToAccount(navigate, brand);
    } catch (err) {
      setError(err?.message || 'Login failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (brandError) return <Centered>{brandError}</Centered>;
  if (!brand) return <Centered>Loading…</Centered>;
  if (!brand.customer_login_enabled) {
    return <Centered>This brand does not have customer login.</Centered>;
  }

  return (
    <div style={wrap}>
      <form style={cardStyle} onSubmit={submit}>
        <BrandHeader brand={brand} />
        <h2 style={{ margin: 0, marginBottom: 18, fontSize: 22, color: '#0f172a' }}>Sign in</h2>
        <input
          type='email' placeholder='Email' value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={inputStyle} autoComplete='email' required
        />
        <input
          type='password' placeholder='Password' value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={inputStyle} autoComplete='current-password' required
        />
        {error && <div style={{ color: '#dc2626', fontSize: 13, marginBottom: 10 }}>{error}</div>}
        <button type='submit' style={primaryBtn(accentColor)} disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>

        {/* Below the password form, not above it. The brand's own customers
            mostly have a password here; the social buttons are the
            alternative, and putting them first makes the ordinary path look
            like the afterthought. */}
        <SocialButtons brandId={brand._id} returnTo='/account' />
        {brand.customer_self_signup_enabled && (
          <div style={{ textAlign: 'center', fontSize: 13, color: '#64748b', marginTop: 14 }}>
            New here?{' '}
            <button
              type='button'
              style={{
                color: accentColor, fontWeight: 600,
                background: 'none', border: 'none', padding: 0,
                cursor: 'pointer', textDecoration: 'underline',
              }}
              onClick={() => navigateToSignup(navigate, brand)}
            >
              Create an account
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
